import React, { useEffect, useState } from "react";
import {
  Dialog,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Collapse,
  Box,
  IconButton,
  useMediaQuery,
  useTheme,
  ListItemIcon,
} from "@mui/material";
import FacebookIcon from '@mui/icons-material/Facebook';
import { ExpandLess, ExpandMore, Menu as MenuIcon } from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PeopleIcon from "@mui/icons-material/People";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import GroupsIcon from "@mui/icons-material/Groups";
import InventoryIcon from "@mui/icons-material/Inventory";
import MailIcon from "@mui/icons-material/Mail";
import HandshakeIcon from "@mui/icons-material/Handshake";
import HistoryIcon from "@mui/icons-material/History";
import NotificationsIcon from "@mui/icons-material/Notifications";
import BusinessIcon from "../../assets/icons/ProjectIcon.png";
import AssignmentIcon from "../../assets/icons/LeadIcon.png";
import PlanUpgradeDialog from "../../utils/PlanUpgradeDialog";
import { useOrganization } from "../../utils/OrganizationContext";
import NotificationWarning from "../../utils/NotificationWarning";
import { fetchFeatures } from "../../utils/fetchFeatures";

const drawerWidth = 240;
const NAVBAR_HEIGHT = 56;

// Direction C tokens
const FONT = "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif";
const INK = "#0F1B2A";
const INK_2 = "#3D4C60";
const MUTED = "#66748A";
const LINE = "#E4E9EF";
const PRIMARY = "#0B5C8A";

const paperSx = {
  backgroundColor: "#FFFFFF",
  color: INK,
  borderRight: `1px solid ${LINE}`,
  fontFamily: FONT,
};

const listSx = { px: 1, py: 1.25, display: "flex", flexDirection: "column", gap: "2px" };

// Plain nav row: quiet at rest, tinted + bold when selected.
const itemSx = {
  borderRadius: "9px",
  py: 0.875,
  px: 1.25,
  minHeight: 38,
  color: INK_2,
  "& .MuiListItemText-root": { m: 0 },
  "& .MuiListItemText-primary": {
    fontFamily: FONT,
    fontSize: 14,
    fontWeight: 500,
    lineHeight: 1.3,
  },
  "& .MuiListItemIcon-root": { minWidth: 32, color: MUTED },
  "& img": { opacity: 0.75 },
  "&:hover": { backgroundColor: "#F4F7FA", color: INK },
  "&.Mui-selected, &.Mui-selected:hover": {
    backgroundColor: "#EAF2FB",
    color: INK,
    "& .MuiListItemText-primary": { fontWeight: 700 },
    "& .MuiListItemIcon-root": { color: PRIMARY },
    "& .MuiSvgIcon-root": { color: `${PRIMARY} !important` },
    "& img": { opacity: 1 },
  },
};

// "Create" group header: the primary action, so it gets the primary fill.
const createSx = {
  mb: 0.75,
  borderRadius: "10px",
  py: 1,
  px: 1.375,
  minHeight: 40,
  background: PRIMARY,
  color: "#FFFFFF",
  "& .MuiListItemText-root": { m: 0 },
  "& .MuiListItemText-primary": {
    fontFamily: FONT,
    fontSize: 14,
    fontWeight: 700,
  },
  "&:hover": { background: "#084767" },
};

const childSx = {
  ...itemSx,
  pl: 5.25,
  minHeight: 36,
  py: 0.75,
  "& .MuiListItemText-primary": {
    fontFamily: FONT,
    fontSize: 13.5,
    fontWeight: 500,
    lineHeight: 1.3,
  },
};

// Design: each nav row is a coloured dot + label + count (no icons).
const DOT_COLORS = {
  Dashboard: "#1F6FEB",
  Leads: "#1FA386",
  Reminders: "#E0A21B",
  "Project Dashboard": "#1E9CC7",
  Projects: "#1F6FEB",
  Inventory: "#2FA864",
  Partners: "#6E5FE0",
  Users: "#DB4B8E",
  Teams: "#1F6FEB",
  Invitees: "#1E9CC7",
  "Past Members": "#8A97A8",
  "Connect to Facebook": "#1F6FEB",
  "Lead Structure": "#6E5FE0",
};
const Dot = ({ color, dim }) => (
  <span
    style={{
      flex: "0 0 7px",
      width: 7,
      height: 7,
      marginRight: 11,
      borderRadius: "50%",
      background: dim ? "#C3CDD9" : color || "#8A97A8",
    }}
  />
);
const GROUP_ORDER = ["Work", "Catalog", "Organization", "Integrations", "Configuration"];

const groupLabelSx = {
  px: 1.375,
  pt: 2,
  pb: 0.625,
  fontFamily: FONT,
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: "#8A97A8",
};
const countStyle = {
  marginLeft: 8,
  fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
  fontSize: 11.5,
  fontWeight: 600,
  color: "#8A97A8",
};
const Crown = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style={{ marginLeft: 8, color: "#8E86B8", flex: "0 0 auto" }}>
    <path d="M2.4 7.1a1.1 1.1 0 0 1 1.72-.9l3.1 2.2L10.9 3.6a1.3 1.3 0 0 1 2.2 0l3.68 4.8 3.1-2.2a1.1 1.1 0 0 1 1.72.9L19.9 17.3a1 1 0 0 1-.98.8H5.08a1 1 0 0 1-.98-.8L2.4 7.1zM5 19.6h14a1 1 0 0 1 0 2H5a1 1 0 0 1 0-2z" />
  </svg>
);

const MENU_ITEMS = [
  {
    label: "Create",
    icon: AddCircleIcon,
    roles: ["ADMIN"],
    children: [
      {
        label: "Add User",
        path: "/dashboard/create-user",
        roles: ["ADMIN"],
      },
      {
        label: "Add Team",
        path: "/dashboard/create-team",
        roles: ["ADMIN"],
      },
      {
        label: "Add Project",
        path: "/dashboard/create-project",
        roles: ["ADMIN"],
      },
      {
        label: "Add Lead",
        path: "/dashboard/add-lead",
        roles: ["ADMIN"],
      },
      {
        label: "Add Inventory",
        path: "/dashboard/inventory/add-inventory",
        roles: ["ADMIN"],
      },
      {
        label: "Lead Structure",
        path: "/dashboard/create-structure",
        feature: "LEAD_STRUCTURE",
        roles: ["ADMIN"],
      },
      {
        label: "Add Partner",
        path: "/dashboard/add-partner",
        roles: ["ADMIN"],
      },
    ],
  },
  {
    label: "Dashboard",
    group: "Work",
    countKey: "today",
    path: "/dashboard",
    icon: DashboardIcon,
    roles: ["ADMIN"],
  },
  {
    label: "Users",
    group: "Organization",
    countKey: "users",
    path: "/dashboard/users",
    icon: PeopleIcon,
    roles: ["ADMIN", "PROJECT_HEAD", "SALESPERSON", "TELECALLER"],
  },
  {
    label: "Teams",
    group: "Organization",
    countKey: "teams",
    path: "/dashboard/teams",
    icon: GroupsIcon,
    roles: ["ADMIN"],
  },
  {
    label: "Projects",
    group: "Catalog",
    countKey: "projects",
    path: "/dashboard/projects",
    icon: BusinessIcon,
    roles: ["ADMIN"],
  },
  {
    label: "Inventory",
    group: "Catalog",
    path: "/dashboard/inventory",
    icon: InventoryIcon,
    roles: ["ADMIN"],
  },
  {
    label: "Leads",
    group: "Work",
    path: "/dashboard/leads",
    icon: AssignmentIcon,
    roles: ["ADMIN"],
  },
  {
    label: "Connect to Facebook", 
    group: "Integrations",
    path: "/dashboard/integrations/facebook",
    icon: FacebookIcon,
    roles: ["ADMIN"],
  },
  {
    label: "Invitees",
    group: "Organization",
    path: "/dashboard/invites",
    icon: MailIcon,
    roles: ["ADMIN"],
  },
  {
    label: "Partners",
    group: "Catalog",
    countKey: "partners",
    path: "/dashboard/partners",
    icon: HandshakeIcon,
    roles: ["ADMIN"],
  },
  {
    label: "Past Members",
    group: "Organization",
    path: "/dashboard/past-members",
    icon: HistoryIcon,
    roles: ["ADMIN"],
  },
  {
    label: "Reminders",
    group: "Work",
    countKey: "reminders",
    path: "/dashboard/reminder",
    icon: NotificationsIcon,
    roles: ["ADMIN"],
  },
  {
    label: "Lead Structure",
    path: "/dashboard/create-structure",
    group: "Configuration",
    feature: "LEAD_STRUCTURE",
    roles: ["ADMIN"],
  },
];

const AdminSidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [openCreate, setOpenCreate] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [mobileOpen, setMobileOpen] = useState(false);
  const storedUser = JSON.parse(localStorage.getItem("user"));
  const {role} = useOrganization();
  const subs = JSON.parse(localStorage.getItem("subs"));

  const orgId = JSON.parse(localStorage.getItem("org"));
  const [counts, setCounts] = useState({});
  const [locked, setLocked] = useState({});
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgradeTarget, setUpgradeTarget] = useState(null);

  // Re-render when the plan changes (payment success) so the crowns re-check the new plan.
  const [, setPlanTick] = useState(0);
  useEffect(() => {
    const onPlanChanged = () => setPlanTick((t) => t + 1);
    window.addEventListener("plan-changed", onPlanChanged);
    return () => window.removeEventListener("plan-changed", onPlanChanged);
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen((prev) => !prev);
  };

  // Live counts — each call is one this app already makes elsewhere.
  useEffect(() => {
    if (!orgId || !storedUser?.accessToken) return;
    const headers = { Authorization: `Bearer ${storedUser.accessToken}` };
    const base = process.env.REACT_APP_BACKEND_URL;
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const sources = {
      today: [`${base}/lead/today?orgId=${orgId}`, (d) => d.length],
      reminders: [`${base}/reminder/all?orgId=${orgId}`, (d) => d.filter((r) => new Date(r.dateTime) >= startOfToday).length],
      projects: [`${base}/project/all?orgId=${orgId}`, (d) => d.length],
      partners: [`${base}/partner/all?orgId=${orgId}`, (d) => d.length],
      teams: [`${base}/team/all?orgId=${orgId}`, (d) => d.length],
      users: [`${base}/org-user/all?orgId=${orgId}&username=${storedUser.username}`, (d) => d.length],
    };
    let cancelled = false;
    Object.entries(sources).forEach(([key, [url, pick]]) => {
      axios
        .get(url, { headers })
        .then((res) => {
          if (!cancelled && Array.isArray(res.data)) {
            setCounts((prev) => ({ ...prev, [key]: pick(res.data) }));
          }
        })
        .catch(() => {});
    });
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  // Crown lock for plan-gated items, from the plan's feature list.
  useEffect(() => {
    const keys = [...new Set(MENU_ITEMS.flatMap((i) => [i, ...(i.children || [])]).map((i) => i.feature).filter(Boolean))];
    if (!keys.length || !storedUser?.accessToken) return;
    // Plan not known yet (navbar still loading the org) → don't lock anything.
    if (!subs) {
      setLocked({});
      return;
    }
    // PRO includes everything — no need to ask.
    if (String(subs).toUpperCase() === "PRO") {
      setLocked({});
      return;
    }
    let cancelled = false;
    keys.forEach(async (key) => {
      try {
        const ok = await fetchFeatures(key, subs, storedUser.accessToken);
        if (!cancelled) setLocked((prev) => ({ ...prev, [key]: !ok }));
      } catch (e) {
        // leave unlocked on error so nobody gets blocked by a failed check
      }
    });
    return () => {
      cancelled = true;
    };
    // Re-check whenever the org or its plan changes (the navbar writes
    // "subs" to localStorage after it loads the org).
  }, [orgId, subs]);

  const go = (item, closeDrawer) => {
    // Locked (paid-plan) items open the plan dialog instead of navigating.
    if (item.feature && locked[item.feature]) {
      setUpgradeTarget(item.feature);
      setUpgradeOpen(true);
    } else {
      navigate(item.path);
    }
    if (closeDrawer) handleDrawerToggle();
  };

  const trailing = (item) =>
    item.feature && locked[item.feature] ? (
      <Crown />
    ) : item.countKey && counts[item.countKey] !== undefined ? (
      <span style={countStyle}>{counts[item.countKey]}</span>
    ) : null;

  const handleCreateToggle = () => {
    setOpenCreate((prev) => !prev);
  };

  useEffect(() => {
    const getUser = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/partner/details`,
          {
            headers: { Authorization: `Bearer ${storedUser.accessToken}` },
          },
        );
        if (res.data) {
          MENU_ITEMS.concat({
            label: "Partner Dashboard",
            path: "/dashboard/partner/leads",
            roles: ["PROJECT_HEAD", "SALESPERSON", "TELECALLER"],
          });
        }
      } catch (error) {
        console.log(error);
      }
    };
    getUser();
  }, []);

  const renderMenu = (closeDrawer) => {
    const visible = MENU_ITEMS.filter((item) => item.roles.includes(role));
    const createItems = visible.filter((i) => i.children);
    const rows = visible.filter((i) => !i.children);

    const row = (item) => (
      <ListItemButton
        key={item.path}
        selected={location.pathname === item.path}
        onClick={() => go(item, closeDrawer)}
        sx={itemSx}
      >
        <Dot color={DOT_COLORS[item.label]} dim={item.feature && locked[item.feature]} />

        <ListItemText primary={item.label} />
        {trailing(item)}
      </ListItemButton>
    );

    return (
      <List sx={listSx}>
        {[].map((item) => {
          const visibleChildren = item.children.filter((c) =>
            c.roles.includes(role),
          );

          if (visibleChildren.length === 0) return null;

          return (
            <Box key={item.label}>
              <ListItemButton onClick={handleCreateToggle} sx={createSx}>
                {item.icon && (
                  <ListItemIcon sx={{ minWidth: "32px" }}>
                    <item.icon sx={{ color: "#fff", fontSize: 19 }} />
                  </ListItemIcon>
                )}

                <ListItemText primary={item.label} />
                {openCreate ? (
                  <ExpandLess sx={{ color: "#fff", fontSize: 20 }} />
                ) : (
                  <ExpandMore sx={{ color: "#fff", fontSize: 20 }} />
                )}
              </ListItemButton>

              <Collapse in={openCreate} timeout="auto" unmountOnExit>
                <List component="div" disablePadding sx={{ display: "flex", flexDirection: "column", gap: "2px", mb: 0.75 }}>
                  {visibleChildren.map((child) => (
                    <ListItemButton
                      key={child.path}
                      selected={location.pathname === child.path}
                      onClick={() => go(child, closeDrawer)}
                      sx={childSx}
                    >
                      <ListItemText primary={child.label} />
                      {trailing(child)}
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            </Box>
          );
        })}

        {GROUP_ORDER.map((g) => {
          const items = rows.filter((i) => i.group === g);
          if (!items.length) return null;
          return (
            <Box key={g} sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
              <Box sx={groupLabelSx}>{g}</Box>
              {items.map(row)}
            </Box>
          );
        })}
        {rows.filter((i) => !i.group).map(row)}
      </List>
    );
  };

  const drawerContent = renderMenu(true);

  return (
    <>
      {/* Hamburger button (use inside AppBar if you have one) */}
      {isMobile && (
        <IconButton
          onClick={handleDrawerToggle}
          sx={{
            position: "absolute",
            top: 10,
            left: 12,
            zIndex: 21,
            width: 36,
            height: 36,
            background: "#FFFFFF",
            border: `1px solid ${LINE}`,
            borderRadius: "10px",
            color: INK,
            "&:hover": { background: "#F4F7FA" },
          }}
        >
          <MenuIcon sx={{ fontSize: 20 }} />
        </IconButton>
      )}
      {/* Mobile Drawer */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              ...paperSx,
            },
          }}
        >
          <Box sx={{ px: 1.25, pt: 1.25, "& > div": { mb: "4px !important" } }}><NotificationWarning /></Box>
          {drawerContent}
        </Drawer>
      )}
      {!isMobile && (
        <Drawer
          className="sidebar"
          variant="permanent"
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              boxSizing: "border-box",
              top: 0,
              height: "100%",
              ...paperSx,
            },
          }}
        >
          {/* Continues the navbar's bottom rule across the sidebar column */}
          <Box sx={{ flex: "0 0 auto", height: `${NAVBAR_HEIGHT}px`, borderBottom: `1px solid ${LINE}` }} />
          <Box sx={{ px: 1.25, pt: 1.25, "& > div": { mb: "4px !important" } }}><NotificationWarning /></Box>
          <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>{renderMenu(false)}</Box>
          <hr style={{ width: "calc(100% - 32px)", margin: "4px 16px 12px", border: 0, borderTop: "1px solid #EDF1F5" }} />
          {/* {subs !== "PRO" && <UpgradeModal />} */}
        </Drawer>
      )}
      <PlanUpgradeDialog
        open={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        featureKey={upgradeTarget}
        reason="This feature is part of a paid plan."
      />
    </>
  );
};

export default AdminSidebar;